import {
  Card,
  Divider,
  List,
  Tag,
  Typography,
  Space,
  Select,
  Empty,
  Row,
  Col,
  Spin,
} from "antd";
import { useEffect, useState } from "react";
import AddBin from "./AddBin";
import bin_img from "../../../assets/dashboard/bin_img.png";
import ManageBinService from "../../../services/ManageBinService";
import { useNavigate } from "react-router";
import { InboxOutlined } from "@ant-design/icons";

const { Option } = Select;

const Dashboard = ({ userData }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [displayBin, setDisplayBin] = useState([]);
  const [filteredBins, setFilteredBins] = useState([]);
  const [selectedLine, setSelectedLine] = useState(null);
  const [loading, setLoading] = useState(true);

  const navigate = useNavigate();

  const today = new Date();
  const formattedDate = `${today.getDate().toString().padStart(2, "0")}.${(
    today.getMonth() + 1
  )
    .toString()
    .padStart(2, "0")}.${today.getFullYear()}`;

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        const binResponse = await ManageBinService.getBinData();
        const allBins = Array.isArray(binResponse?.data) ? binResponse.data : [];

        const storedUserData = JSON.parse(localStorage.getItem("userData"));
        let binData = allBins;

        if (storedUserData?.data?.dept === "21") {
          binData = allBins.filter(
            (bin) =>
              bin.bin_location === "dispatch_center" &&
              bin.bin_full === "yes"
          );
        }

        setDisplayBin(binData);
        setFilteredBins(binData);
      } catch (error) {
        console.error("Error fetching data:", error);
        setDisplayBin([]);
        setFilteredBins([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleAddBin = (binData) => {
    setDisplayBin((prevBins) => [...prevBins, binData]);
    setFilteredBins((prevBins) => [...prevBins, binData]);
  };

  const handleCardClick = (bin) => {
    navigate(
      `/dc-manage-bin/view-garment?bin_id=${encodeURIComponent(
        bin.bin_id
      )}&bin_location=${encodeURIComponent(bin.bin_location)}`
    );
  };

  const uniqueLines = [
    ...new Set(displayBin.map((bin) => bin.bin_line || "Unknown Line")),
  ];

  const sortedLines = uniqueLines.sort((a, b) => {
    const getLineNumber = (line) => {
      if (line === "Unknown Line" || line === "LINE 0") return Infinity;
      const match = line.match(/\d+/);
      return match ? parseInt(match[0], 10) : 0;
    };

    return getLineNumber(a) - getLineNumber(b);
  });

  const filteredByLine = selectedLine
    ? filteredBins.filter((bin) =>
        selectedLine === "Unknown Line" &&
        (!bin.bin_line || bin.bin_line === "LINE 0")
          ? true
          : bin.bin_line === selectedLine
      )
    : filteredBins;

  const totalPresentPcs = filteredByLine.reduce(
    (sum, bin) => sum + Number(bin.total_quantity || 0),
    0
  );

  const binsByLine = {};

  filteredByLine.forEach((bin) => {
    const line =
      bin.bin_line === "LINE 0" || !bin.bin_line
        ? "Unknown Line"
        : bin.bin_line;

    if (!binsByLine[line]) binsByLine[line] = [];
    binsByLine[line].push(bin);
  });

  const getLocationConfig = (location) => {
    switch (location) {
      case "washing":
        return {
          label: "Washing",
          bg: "#e0f2fe",
          color: "#075985",
          border: "#bae6fd",
        };

      case "dispatch_center":
        return {
          label: "Dispatch Center",
          bg: "#f3e8ff",
          color: "#6b21a8",
          border: "#d8b4fe",
        };

      case "garment_super_market":
        return {
          label: "G S M",
          bg: "#fce7f3",
          color: "#9d174d",
          border: "#f9a8d4",
        };

      case "In Transit":
        return {
          label: "In Transit",
          bg: "#fef9c3",
          color: "#854d0e",
          border: "#fde68a",
        };

      default:
        return {
          label: "Unknown",
          bg: "#f1f5f9",
          color: "#475569",
          border: "#cbd5e1",
        };
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div className="page-title-wrap">
          <Typography.Title level={3} className="page-title">
            Dispatch Center Dashboard
          </Typography.Title>

          <Typography.Text className="page-subtitle">
            Manage dispatch center bins
          </Typography.Text>
        </div>
      </div>

      <Divider className="page-divider" />

      <AddBin
        userData={userData}
        addBinModal={isModalOpen}
        setIsModalOpen={setIsModalOpen}
        onAddBin={handleAddBin}
      />

      {loading ? (
        <div
          style={{
            minHeight: "300px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Spin size="large" tip="Loading bins..." />
        </div>
      ) : (
        <>
          <div className="compact-stats" style={{ marginBottom: 24 }}>
            <Row gutter={[16, 8]} justify="center">
              <Col xs={12} sm={6}>
                <div className="compact-stat-item">
                  <div
                    className="compact-stat-value"
                    style={{ color: "#475569" }}
                  >
                    {filteredByLine.length}
                  </div>
                  <div className="compact-stat-label">Present Bins</div>
                </div>
              </Col>

              <Col xs={12} sm={6}>
                <div className="compact-stat-item">
                  <div
                    className="compact-stat-value"
                    style={{ color: "#1d4ed8" }}
                  >
                    {totalPresentPcs}
                  </div>
                  <div className="compact-stat-label">Total Present Pcs</div>
                </div>
              </Col>
            </Row>
          </div>

          <Card
            className="modern-card"
            style={{ marginBottom: 24 }}
            title={
              <Space>
                <InboxOutlined />
                <Typography.Text strong>Bin Overview</Typography.Text>
              </Space>
            }
            extra={
              <Space>
                <Tag className="filter-tag" color="blue">
                  Date: {formattedDate}
                </Tag>

                <Select
                  placeholder="Select Line"
                  style={{ width: 160 }}
                  onChange={(value) => setSelectedLine(value || null)}
                  allowClear
                  value={selectedLine}
                >
                  {sortedLines.map((line) => (
                    <Option key={line} value={line}>
                      {line}
                    </Option>
                  ))}
                </Select>
              </Space>
            }
          >
            <div style={{ marginTop: 8 }}>
              {filteredByLine.length > 0 ? (
                sortedLines.map((line) => {
                  if (!binsByLine[line] || binsByLine[line].length === 0) {
                    return null;
                  }

                  return (
                    <div key={line} style={{ marginBottom: 20 }}>
                      <Typography.Text
                        strong
                        style={{
                          fontSize: "18px",
                          marginBottom: "10px",
                          display: "block",
                          color: "#1e293b",
                        }}
                      >
                        {line}
                      </Typography.Text>

                      <List
                        grid={{
                          gutter: 16,
                          xs: 1,
                          sm: 2,
                          md: 3,
                          lg: 4,
                          xl: 5,
                        }}
                        dataSource={binsByLine[line]}
                        renderItem={(bin) => {
                          const locationConfig = getLocationConfig(
                            bin.bin_location
                          );

                          return (
                            <List.Item>
                              <div
                                className="bin-card"
                                onClick={() => handleCardClick(bin)}
                              >
                                <div className="bin-card-top">
                                  <span
                                    className="location-badge"
                                    style={{
                                      background: locationConfig.bg,
                                      color: locationConfig.color,
                                      borderColor: locationConfig.border,
                                    }}
                                  >
                                    {locationConfig.label}
                                  </span>
                                </div>

                                <div className="bin-card-image-wrap">
                                  <img
                                    src={bin_img}
                                    alt="bin"
                                    className="bin-card-image"
                                  />

                                  <div className="bin-card-name">
                                    <strong>{bin.bin_name}</strong>
                                  </div>
                                </div>

                                <div className="bin-card-footer">
                                  <div
                                    className={`pcs-badge ${
                                      bin.bin_full === "yes"
                                        ? "full-badge"
                                        : "empty-badge"
                                    }`}
                                  >
                                    {bin.total_quantity || 0} Pcs
                                  </div>
                                </div>
                              </div>
                            </List.Item>
                          );
                        }}
                      />
                    </div>
                  );
                })
              ) : (
                <Empty description="No bins found" />
              )}
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default Dashboard;